import React, { useEffect, useMemo, useState } from "react";
import { toastError } from "@/lib/toast";
import {
  createSocialDmGroup,
  getOrCreateSocialDm,
  listSocialMutualFollows,
} from "@/lib/socialDmApi";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import "@/styles/social-messages.css";

function normalizeSearchQuery(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/^@+/, "");
}

function matchesPerson(u, q) {
  if (!q) return true;
  const name = String(u.display_name || "").toLowerCase();
  const handle = String(u.username || "")
    .toLowerCase()
    .replace(/^@+/, "");
  return name.includes(q) || handle.includes(q);
}

function PersonLabel({ user }) {
  const name = user.display_name || user.username || "User";
  const handle = user.username
    ? String(user.username).replace(/^@+/, "")
    : null;
  return (
    <span className="min-w-0 flex items-baseline gap-1.5 truncate">
      <span className="font-medium truncate text-foreground">{name}</span>
      {handle ? (
        <span className="text-xs italic text-muted-foreground truncate shrink-0">
          @{handle}
        </span>
      ) : null}
    </span>
  );
}

export default function SocialNewDmChatDialog({
  open,
  onOpenChange,
  accessToken,
  currentUserId,
  onCreated,
}) {
  const [memberQuery, setMemberQuery] = useState("");
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [starting, setStarting] = useState(false);

  const reset = () => {
    setMemberQuery("");
    setSelectedMemberIds([]);
  };

  useEffect(() => {
    if (!open || !accessToken) return;
    let cancelled = false;
    const q = normalizeSearchQuery(memberQuery);
    const timer = setTimeout(async () => {
      setMembersLoading(true);
      try {
        const users = await listSocialMutualFollows(accessToken, q);
        if (!cancelled) setMembers(users || []);
      } catch (err) {
        console.error("Failed to load mutual follows:", err);
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    }, q ? 200 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, accessToken, memberQuery]);

  const filteredMembers = useMemo(() => {
    const q = normalizeSearchQuery(memberQuery);
    return (members || [])
      .filter((u) => u.user_id !== currentUserId)
      .filter((u) => matchesPerson(u, q));
  }, [members, memberQuery, currentUserId]);

  const selectedCount = selectedMemberIds.length;
  const isGroup = selectedCount >= 2;
  const canStart = selectedCount >= 1;

  const toggleMember = (userId) => {
    setSelectedMemberIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange?.(next);
  };

  const handleStart = async () => {
    if (!accessToken || !canStart || starting) return;
    setStarting(true);
    try {
      let conv;
      if (selectedCount === 1) {
        conv = await getOrCreateSocialDm(accessToken, selectedMemberIds[0]);
      } else {
        conv = await createSocialDmGroup(accessToken, {
          memberUserIds: selectedMemberIds,
        });
      }
      handleOpenChange(false);
      await onCreated?.(conv);
    } catch (err) {
      console.error("Failed to start social chat:", err);
      toastError(err, "Could not start conversation");
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-social-messages>
        <DialogHeader>
          <DialogTitle>New message</DialogTitle>
          <DialogDescription>
            Message someone you follow mutually. Select several people to start
            a group. Search by name or @handle.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={memberQuery}
          onChange={(e) => setMemberQuery(e.target.value)}
          placeholder="Search by name or @handle…"
          autoFocus
          className="rounded-xl"
        />
        <div className="max-h-64 overflow-y-auto rounded-xl border border-[hsl(var(--sm-divider))] divide-y divide-[hsl(var(--sm-divider))]">
          {membersLoading ? (
            <p className="p-3 text-sm text-muted-foreground">Loading…</p>
          ) : filteredMembers.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">
              No mutual follows found
            </p>
          ) : (
            filteredMembers.map((u) => {
              const selected = selectedMemberIds.includes(u.user_id);
              return (
                <button
                  key={u.user_id}
                  type="button"
                  disabled={starting}
                  className={cn(
                    "w-full text-left px-3 py-2.5 text-sm disabled:opacity-50 flex items-center justify-between gap-2 transition-colors",
                    selected
                      ? "bg-[hsl(var(--sm-accent)/0.12)]"
                      : "hover:bg-[hsl(var(--sm-accent)/0.06)]"
                  )}
                  onClick={() => toggleMember(u.user_id)}
                >
                  <span className="min-w-0 flex items-center gap-2 truncate">
                    <SocialUserAvatar
                      user={u}
                      avatarUrl={u.avatar_url}
                      displayName={u.display_name}
                      className="h-8 w-8"
                      fallbackClassName="text-[10px]"
                    />
                    <PersonLabel user={u} />
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {selected ? "Selected" : "Add"}
                  </span>
                </button>
              );
            })
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {selectedCount === 0
              ? "Select people to message"
              : selectedCount === 1
                ? "1 person · direct message"
                : `${selectedCount} people · group`}
          </p>
          <Button
            type="button"
            className="sm-send rounded-full border-0"
            disabled={starting || !canStart}
            onClick={handleStart}
          >
            {isGroup ? "Create group" : "Start chat"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
